import { Loader2 } from "lucide-react"

import { Button } from "shared/ui/button"

export type LayoutPreviewRuntimeToolbarProps = {
  isLoadingPreviewData: boolean
  hasTemplate: boolean
  onReload: () => void
  previewRows: Array<Record<string, unknown>>
  previewRowIndex: number
  onPreviewRowIndexChange: (index: number) => void
  previewDataSource: "api" | "fallback" | null
  previewElapsedMs?: number
  hasWorkersTable: boolean
  previewWorkersCount: number
  previewDataError: string | null
  previewWorkersError: string | null
  isLoadingPreviewWorkers: boolean
}

export function LayoutPreviewRuntimeToolbar({
  isLoadingPreviewData,
  hasTemplate,
  onReload,
  previewRows,
  previewRowIndex,
  onPreviewRowIndexChange,
  previewDataSource,
  previewElapsedMs,
  hasWorkersTable,
  previewWorkersCount,
  previewDataError,
  previewWorkersError,
  isLoadingPreviewWorkers,
}: LayoutPreviewRuntimeToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50/40 p-2 text-sm">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="gap-2"
        onClick={onReload}
        disabled={isLoadingPreviewData || !hasTemplate}
      >
        {isLoadingPreviewData ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Reload Runtime Data
      </Button>

      {previewRows.length > 0 ? (
        <div className="inline-flex items-center gap-2">
          <span className="text-xs text-gray-600">Sample Row</span>
          <select
            className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
            value={previewRowIndex}
            onChange={(event) => {
              const next = Number.parseInt(event.target.value, 10)
              onPreviewRowIndexChange(Number.isFinite(next) ? next : 0)
            }}
          >
            {previewRows.map((row, index) => {
              const permitNumber = String(row.permit_number ?? row.__permitNumber ?? "").trim()
              return (
                <option key={`preview-row-${index}`} value={index}>
                  {permitNumber ? `#${index + 1} ${permitNumber}` : `#${index + 1}`}
                </option>
              )
            })}
          </select>
        </div>
      ) : null}

      <span className="text-xs text-gray-600">
        Rows: {previewRows.length}
        {previewDataSource ? ` | source: ${previewDataSource}` : ""}
        {previewElapsedMs != null ? ` | ${previewElapsedMs} ms` : ""}
        {hasWorkersTable ? ` | workers: ${previewWorkersCount}` : ""}
      </span>

      {previewDataError ? <span className="text-xs text-red-600">{previewDataError}</span> : null}
      {hasWorkersTable && previewWorkersError ? <span className="text-xs text-red-600">{previewWorkersError}</span> : null}
      {hasWorkersTable && isLoadingPreviewWorkers ? (
        <span className="text-xs text-gray-600 inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          loading workers...
        </span>
      ) : null}
    </div>
  )
}
